import React, { useState, useEffect, useCallback, useMemo } from "react";
import { X, CreditCard, CheckCircle2, MapPin, Building2, Users, Calendar as CalendarIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../shadcn/ui/dialog";
import { Button } from "../shadcn/ui/button";
import { Input } from "../shadcn/ui/input";
import { Textarea } from "../shadcn/ui/textarea";
import { Label } from "../shadcn/ui/label";
import { Badge } from "../shadcn/ui/badge";
import { Card, CardContent } from "../shadcn/ui/card";
import { Separator } from "../shadcn/ui/separator";
import { Calendar as ShadcnCalendar } from "../shadcn/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../shadcn/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../shadcn/ui/select";
import useBookingFlow from "../hooks/useBookingFlow";
import toastUtils from "../utils/toast";
import * as localService from "../services/localService";
import teamsService from "../services/host-apiServices/teamsService";
import BookingTypeSelector from "../screens/host-experience/host-calendar/components/BookingTypeSelector";
import AddonSelector from "../components/addons/AddonSelector";
import logger from "../utils/logger";
import {
  getNormalizedDurationType,
  buildBookingTimeline,
  getBookingsForDate,
  isDateBooked as timelineDateBooked,
} from "../utils/bookingTimeframeUtils";

/**
 * Space Details Modal - Airbnb-style Booking Confirmation Design
 */
const HSpaceDetailsModal = ({
  isOpen,
  onClose,
  space,
  selectedDate,
  availableTimeSlots = [],
  onDateChange,
  existingBookings = [],
  userCurrency = "GHS",
  timeFormat = "12h",
  viewOnly = false,
  onBookingSuccess,
  onBookingError,
}) => {
  // Use centralized booking flow hook
  const { bookingError, hasActiveSubscription, handleBookNow, handlePayNow } = useBookingFlow();

  const [selectedStartTime, setSelectedStartTime] = useState(null);
  const [selectedEndTime, setSelectedEndTime] = useState(null);
  const [bookingSelection, setBookingSelection] = useState(null); // For BookingTypeSelector
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [attendeeEmails, setAttendeeEmails] = useState([]);
  const [emailInput, setEmailInput] = useState("");
  const [selectedAddons, setSelectedAddons] = useState([]);

  const formatTime = useCallback(
    date => {
      if (!date || !(date instanceof Date) || isNaN(date.getTime())) return "—";
      const h = date.getHours();
      const m = date.getMinutes().toString().padStart(2, "0");
      if (timeFormat === "24h") {
        return `${h.toString().padStart(2, "0")}:${m}`;
      } else {
        const period = h >= 12 ? "PM" : "AM";
        const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
        return `${displayHour}:${m} ${period}`;
      }
    },
    [timeFormat]
  );

  const formatDate = useCallback(date => {
    if (!date) return "Select a date";
    const normalized = date instanceof Date ? date : new Date(date);
    if (isNaN(normalized.getTime())) return "Select a date";
    return normalized.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }, []);

  const formatDuration = useCallback(minutes => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }, []);

  useEffect(() => {
    if (isOpen) {
      setSelectedStartTime(null);
      setSelectedEndTime(null);
      setBookingSelection(null);
      setNotes("");
      setAttendeeEmails([]);
      setEmailInput("");
      setSelectedAddons([]);
    }
  }, [isOpen]);

  const availableDayNames = useMemo(() => {
    if (!space?.availableSlots) return new Set();
    return new Set(
      space.availableSlots.map(slot => (typeof slot.day === "string" ? slot.day.toUpperCase() : "")).filter(Boolean)
    );
  }, [space?.availableSlots]);

  const startOfToday = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);

  const isDateSelectable = useCallback(
    date => {
      if (!date) return false;
      const dayNames = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];
      const dayName = dayNames[date.getDay()];
      return availableDayNames.has(dayName);
    },
    [availableDayNames]
  );

  const bookingTimeline = useMemo(() => buildBookingTimeline(existingBookings), [existingBookings]);

  const isDateBooked = useCallback(date => timelineDateBooked(date, bookingTimeline), [bookingTimeline]);

  const bookingsForSelectedDate = useMemo(() => {
    if (!selectedDate) return [];
    return getBookingsForDate(selectedDate, bookingTimeline);
  }, [selectedDate, bookingTimeline]);

  const getAvailableTimeOptions = useCallback(() => {
    if (!availableTimeSlots || availableTimeSlots.length === 0) return [];
    return availableTimeSlots
      .filter(slot => slot.available !== false)
      .map(slot => ({
        time: formatTime(slot.startTime),
        dateTime: slot.startTime,
        label: formatTime(slot.startTime),
      }));
  }, [availableTimeSlots, formatTime]);

  const getEndTimeOptions = useCallback(() => {
    if (!selectedStartTime || !availableTimeSlots || availableTimeSlots.length === 0) return [];
    const startSlotIndex = availableTimeSlots.findIndex(slot => formatTime(slot.startTime) === selectedStartTime);
    if (startSlotIndex === -1) return [];

    const endOptions = [];
    const minDurationMinutes = (space?.minimumDuration || 1) * 60;
    const maxDurationMinutes = (space?.maximumDuration || 24) * 60;
    let cumulativeDuration = 0;

    for (let i = startSlotIndex; i < availableTimeSlots.length; i++) {
      const slot = availableTimeSlots[i];
      cumulativeDuration += 30;
      if (slot.available === false) break;
      if (cumulativeDuration >= minDurationMinutes && cumulativeDuration <= maxDurationMinutes) {
        endOptions.push({
          time: formatTime(slot.endTime),
          dateTime: slot.endTime,
          label: formatTime(slot.endTime),
          duration: cumulativeDuration,
          durationLabel: formatDuration(cumulativeDuration),
        });
      }
      if (cumulativeDuration >= maxDurationMinutes) break;
    }
    return endOptions;
  }, [
    availableTimeSlots,
    selectedStartTime,
    space?.minimumDuration,
    space?.maximumDuration,
    formatTime,
    formatDuration,
  ]);

  const getBookingDetails = useCallback(() => {
    if (bookingSelection && bookingSelection.startTime && bookingSelection.endTime) {
      const durationMs = bookingSelection.endTime.getTime() - bookingSelection.startTime.getTime();
      const durationMinutes = Math.floor(durationMs / (1000 * 60));
      return {
        startTime: bookingSelection.startTime,
        endTime: bookingSelection.endTime,
        duration: durationMinutes,
        durationLabel: formatDuration(durationMinutes),
      };
    }

    if (!selectedStartTime || !selectedEndTime || !space) return null;
    const startSlot = availableTimeSlots.find(s => formatTime(s.startTime) === selectedStartTime);
    const endSlot = availableTimeSlots.find(s => formatTime(s.endTime) === selectedEndTime);
    if (!startSlot || !endSlot) return null;

    const durationMs = endSlot.endTime.getTime() - startSlot.startTime.getTime();
    const durationMinutes = Math.floor(durationMs / (1000 * 60));

    return {
      startTime: startSlot.startTime,
      endTime: endSlot.endTime,
      duration: durationMinutes,
      durationLabel: formatDuration(durationMinutes),
    };
  }, [bookingSelection, selectedStartTime, selectedEndTime, space, availableTimeSlots, formatTime, formatDuration]);

  const bookingDetails = useMemo(
    () => getBookingDetails(),
    [getBookingDetails, bookingSelection, selectedStartTime, selectedEndTime, availableTimeSlots, space]
  );

  const hourlyRate = space?.propertyPrice?.price || space?.price || space?.ratePrice || 0;
  const monthlyRate = space?.propertyPrice?.price || space?.price || space?.ratePrice || 0;
  const durationHours = bookingDetails ? bookingDetails.duration / 60 : 0;

  const durationType = getNormalizedDurationType(space);
  const isMonthlyDuration = durationType === "MONTHLY";
  const monthlyPeriods = useMemo(() => {
    if (!isMonthlyDuration || !bookingSelection?.startTime || !bookingSelection?.endTime) {
      return 0;
    }
    const diffMs = bookingSelection.endTime.getTime() - bookingSelection.startTime.getTime();
    const diffDays = Math.max(1, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
    const computedMonths = Math.max(1, Math.ceil(diffDays / 30));
    const minMonths = Math.max(1, space?.minimumDuration || 1);
    const maxMonths = Math.max(minMonths, space?.maximumDuration || computedMonths);
    return Math.min(Math.max(computedMonths, minMonths), maxMonths);
  }, [isMonthlyDuration, bookingSelection, space?.minimumDuration, space?.maximumDuration]);
  const basePrice = isMonthlyDuration ? monthlyRate * monthlyPeriods : hourlyRate * durationHours;
  const addonsTotal = selectedAddons.reduce((sum, addon) => sum + addon.price * addon.quantity, 0);
  const totalPrice = basePrice + addonsTotal;

  if (!isOpen || !space) return null;

  const images = space.media?.images || space.images || [];
  const mainImage =
    images.length > 0
      ? typeof images[0] === "string"
        ? images[0]
        : images[0].resourceUrl || images[0].url || null
      : null;
  const amenities = space.amenities || [];
  const location = space.location || {};
  const disableBookedDates = durationType !== "HOURLY";

  const handleAddEmail = () => {
    const email = emailInput.trim();
    if (!email) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email) || attendeeEmails.includes(email)) return;
    setAttendeeEmails(prev => [...prev, email]);
    setEmailInput("");
  };

  const handleRemoveEmail = emailToRemove => {
    setAttendeeEmails(prev => prev.filter(e => e !== emailToRemove));
  };

  const handleEmailKeyPress = e => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleSubmitBooking = async (usePayment = false) => {
    const bookingDetails = getBookingDetails();
    if (!bookingDetails) {
      toastUtils.error("Please complete the booking selection");
      return;
    }

    // Check for conflicts if using BookingTypeSelector
    if (bookingSelection && bookingSelection.isValid === false) {
      toastUtils.error("Selected time conflicts with an existing booking. Please choose a different time.");
      return;
    }

    // Get current user
    const currentUser = localService.getUser && localService.getUser();
    if (!currentUser?.id) {
      toastUtils.error("User not found. Please log in again.");
      return;
    }

    // Prepare payload for booking API
    const payload = {
      userId: currentUser.id.toString(), // Convert to string as per API spec
      spaceId: space.id,
      startTime: bookingDetails.startTime,
      endTime: bookingDetails.endTime,
      emails: attendeeEmails,
      notes: notes,
      addons: selectedAddons.map(a => ({ addonId: a.addonId, quantity: a.quantity })),
    };

    // Use centralized booking handler
    let bookingResponse;
    let bookingOperation;
    const result = await (usePayment ? handlePayNow : handleBookNow)(payload, {
      space: usePayment ? space : undefined, // Only pass space for payment flow
      onSuccess: response => {
        bookingResponse = response;
        bookingOperation = usePayment ? "create booking with pending payment" : "create booking";
      },
      onError: error => {
        // Call parent callback if provided - this will handle showing the error message via messageHandler
        if (onBookingError) {
          onBookingError(error, usePayment ? "create booking with pending payment" : "create booking");
        }

        logger.error("Booking error:", error);
        // Modal stays open so user can try payment option
      },
    });

    if (onBookingSuccess && bookingResponse) {
      onBookingSuccess(bookingResponse, bookingOperation, result);
    }

    if (!result?.success) {
      return result;
    }

    // Check if this is a PLAY service with ticket URL (handled in hook)
    if (result?.isPlayService && result?.ticketUrl) {
      toastUtils.success("Booking created successfully! Redirecting...");
      setTimeout(() => {
        window.location.href = result.ticketUrl;
      }, 1500);
      return result; // Don't close modal, redirecting
    }

    if (usePayment) {
      if (result?.paymentResult?.success) {
        toastUtils.success("Payment initiated", {
          description: "Complete payment in the new tab to confirm your booking.",
        });
      }
    } else {
      toastUtils.success("Booking created successfully!");
    }
    onClose();

    return result;
  };

  const startTimeOptions = getAvailableTimeOptions();
  const endTimeOptions = getEndTimeOptions();
  // Form is valid if using BookingTypeSelector (bookingSelection) or old method (selectedStartTime/selectedEndTime)
  // Also check that bookingSelection.isValid is not false (conflicts)
  const isFormValid =
    (bookingSelection &&
      bookingSelection.startTime &&
      bookingSelection.endTime &&
      bookingSelection.isValid !== false) ||
    (selectedStartTime && selectedEndTime && bookingDetails);

  // Calculate pricing

  const checkInDate = bookingSelection?.startTime || selectedDate || null;
  const checkOutDate = bookingSelection?.endTime || selectedDate || null;
  const checkInTimeLabel = bookingSelection?.startTime ? formatTime(bookingSelection.startTime) : selectedStartTime;
  const checkOutTimeLabel = bookingSelection?.endTime ? formatTime(bookingSelection.endTime) : selectedEndTime;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl p-0 gap-0 max-h-[85vh] overflow-hidden">
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-2xl font-semibold text-gray-900">Confirm your booking</DialogTitle>
            <Badge variant="outline" className="text-xs text-gray-600 border-gray-300">
              Step 1 of 2
            </Badge>
          </div>
        </DialogHeader>

        {/* Content */}
        <div className="overflow-y-auto" style={{ maxHeight: "calc(85vh - 160px)" }}>
          <div className="p-6 grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Main Content - 60% (3/5) */}
            <div className="lg:col-span-3 space-y-5">
              {/* Property Header */}
              <div className="space-y-1">
                <h2 className="text-xl font-bold text-gray-900">{space.name}</h2>
                <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                  <MapPin className="h-4 w-4 text-gray-500" />
                  <span>{location.city || location.country || "Location"}</span>
                  {space.capacity && (
                    <>
                      <span>•</span>
                      <Users className="h-4 w-4 text-gray-500" />
                      <span>
                        {space.capacity} guest{space.capacity > 1 ? "s" : ""}
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Booking Date Display */}
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-6">
                    <div className="flex-1">
                      <div className="text-xs text-gray-600 mb-1">Check-in</div>
                      <div className="font-semibold text-gray-900">
                        {checkInDate ? formatDate(checkInDate) : <span className="text-gray-400">Select a date</span>}
                        {checkInTimeLabel && (
                          <span className="ml-2 text-blue-700 text-sm font-medium">{checkInTimeLabel}</span>
                        )}
                      </div>
                    </div>
                    <div className="text-2xl text-gray-400 hidden sm:block">→</div>
                    <div className="flex-1">
                      <div className="text-xs text-gray-600 mb-1">Check-out</div>
                      <div className="font-semibold text-gray-900">
                        {checkOutDate ? formatDate(checkOutDate) : <span className="text-gray-400">Select a date</span>}
                        {checkOutTimeLabel && (
                          <span className="ml-2 text-blue-700 text-sm font-medium">{checkOutTimeLabel}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Date Picker */}
              {durationType === "HOURLY" && onDateChange && space?.availableSlots?.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4 text-gray-600" />
                    <Label className="text-sm font-semibold text-gray-700">Select Date</Label>
                  </div>
                  <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="w-full flex justify-between items-center px-4 py-3 text-left bg-white border rounded-xl shadow-sm hover:border-blue-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                      >
                        <span className="text-sm font-medium text-gray-900">
                          {selectedDate ? formatDate(selectedDate) : "Pick a date"}
                        </span>
                        <CalendarIcon className="h-4 w-4 text-gray-500" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0 w-auto" align="start">
                      <div className="p-4 w-[360px]">
                        <div className="flex gap-2 mb-3">
                          <Select
                            value={(selectedDate || new Date()).getMonth().toString()}
                            onValueChange={value => {
                              const month = parseInt(value, 10);
                              const newDate = selectedDate ? new Date(selectedDate) : new Date();
                              newDate.setMonth(month);
                              onDateChange(newDate);
                            }}
                          >
                            <SelectTrigger className="w-full h-9 text-sm">
                              <SelectValue placeholder="Month" />
                            </SelectTrigger>
                            <SelectContent>
                              {Array.from({ length: 12 }).map((_, idx) => (
                                <SelectItem key={idx} value={idx.toString()}>
                                  {new Date(0, idx).toLocaleDateString("en-US", { month: "long" })}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Select
                            value={(selectedDate || new Date()).getFullYear().toString()}
                            onValueChange={value => {
                              const year = parseInt(value, 10);
                              const newDate = selectedDate ? new Date(selectedDate) : new Date();
                              newDate.setFullYear(year);
                              onDateChange(newDate);
                            }}
                          >
                            <SelectTrigger className="w-full h-9 text-sm">
                              <SelectValue placeholder="Year" />
                            </SelectTrigger>
                            <SelectContent className="max-h-60">
                              {Array.from({ length: 61 }).map((_, idx) => {
                                const year = new Date().getFullYear() - 30 + idx;
                                return (
                                  <SelectItem key={year} value={year.toString()}>
                                    {year}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </div>
                        <ShadcnCalendar
                          mode="single"
                          selected={selectedDate}
                          onSelect={date => {
                            if (!date) return;
                            if (!isDateSelectable(date)) {
                              toastUtils.info("No availability on that day");
                              return;
                            }
                            if (disableBookedDates && isDateBooked(date)) {
                              toastUtils.info("This date already has bookings");
                              return;
                            }
                            setIsCalendarOpen(false);
                            onDateChange(date);
                          }}
                          disabled={date => {
                            if (!date || date < startOfToday) return true;
                            if (disableBookedDates && isDateBooked(date)) return true;
                            return false;
                          }}
                          modifiers={{
                            available: date =>
                              date &&
                              date >= startOfToday &&
                              isDateSelectable(date) &&
                              !(disableBookedDates && isDateBooked(date)),
                            booked: date => date && isDateBooked(date),
                          }}
                          modifiersClassNames={{
                            available: "bg-green-50 text-green-700 hover:bg-green-100 hover:text-green-800",
                            booked:
                              durationType === "HOURLY"
                                ? "bg-red-100 text-red-700 hover:bg-red-200 hover:text-red-900 border border-red-200"
                                : "bg-red-100 text-red-600 cursor-not-allowed opacity-70",
                          }}
                        />
                        <p className="mt-2 text-xs text-gray-500">
                          Use the dropdown menus to jump between months or years quickly.
                        </p>
                        {durationType === "HOURLY" && bookingsForSelectedDate.length > 0 && (
                          <div className="mt-3 space-y-2">
                            <Label className="text-xs font-semibold text-gray-600">Booked slots</Label>
                            <div className="flex flex-wrap gap-2">
                              {bookingsForSelectedDate.map(booking => {
                                const start = new Date(booking.startTime);
                                const end = new Date(booking.endTime);
                                const label = `${start.toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })} – ${end.toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}`;
                                return (
                                  <span
                                    key={`${booking.id}-${label}`}
                                    className="px-2 py-1 text-xs rounded-full bg-red-50 text-red-700 border border-red-200"
                                  >
                                    {label}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {/* Booking Section */}
              {!viewOnly && (
                <>
                  {space ? (
                    <BookingTypeSelector
                      durationType={space.duration || "HOURLY"}
                      selectedDate={selectedDate}
                      availableTimeSlots={availableTimeSlots}
                      space={space}
                      minimumDuration={space.minimumDuration || 1}
                      maximumDuration={space.maximumDuration || 24}
                      bufferTimeInMinutes={space.bufferTimeInMinutes || 0}
                      existingBookings={existingBookings}
                      spaceId={space.id}
                      timeFormat={timeFormat}
                      onSelectionChange={setBookingSelection}
                      disabled={false}
                    />
                  ) : (
                    <Card className="bg-amber-50 border-amber-200">
                      <CardContent className="p-4">
                        <p className="text-sm text-amber-800">No space selected. Please select a space to book.</p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Addons Selection */}
                  {space?.addons && space.addons.length > 0 && (
                    <>
                      <Separator />
                      <AddonSelector
                        addons={space.addons}
                        selectedAddons={selectedAddons}
                        onChange={setSelectedAddons}
                        currency={userCurrency}
                      />
                    </>
                  )}

                  <Separator />

                  {/* Notes */}
                  <div>
                    <Label htmlFor="notes" className="text-sm font-semibold mb-2 block">
                      Add a note <span className="text-xs font-normal text-gray-500">(optional)</span>
                    </Label>
                    <Textarea
                      id="notes"
                      value={notes}
                      onChange={e => setNotes(e.target.value)}
                      placeholder="Any special requirements or requests..."
                      rows={2}
                      className="resize-none"
                    />
                  </div>

                  {/* Attendees */}
                  <div>
                    <Label htmlFor="email" className="text-sm font-semibold mb-2 block">
                      Invite attendees <span className="text-xs font-normal text-gray-500">(optional)</span>
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        id="email"
                        type="email"
                        value={emailInput}
                        onChange={e => setEmailInput(e.target.value)}
                        onKeyPress={handleEmailKeyPress}
                        placeholder="name@company.com"
                      />
                      <Button type="button" onClick={handleAddEmail} variant="outline" size="sm">
                        Add
                      </Button>
                    </div>
                    {attendeeEmails.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {attendeeEmails.map((email, index) => (
                          <Badge key={index} variant="secondary" className="pl-2 pr-1 py-1">
                            {email}
                            <X
                              className="h-3 w-3 ml-1.5 cursor-pointer hover:text-red-600"
                              onClick={() => handleRemoveEmail(email)}
                            />
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Amenities - Compact (moved below booking for better grouping) */}
              {amenities.length > 0 && (
                <>
                  <Separator className="my-6" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2 text-sm">What this place offers</h3>
                    <div className="flex flex-wrap gap-2">
                      {amenities.slice(0, 6).map((amenity, index) => {
                        const label =
                          typeof amenity === "string"
                            ? amenity
                            : amenity.label || amenity.name || `Amenity ${index + 1}`;
                        return (
                          <Badge key={amenity.id || index} variant="primary" className="text-xs">
                            {label}
                          </Badge>
                        );
                      })}
                      {amenities.length > 6 && (
                        <Badge variant="secondary" className="text-xs bg-white">
                          +{amenities.length - 6} more
                        </Badge>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Sidebar - 40% (2/5) */}
            <div className="lg:col-span-2">
              <Card className="sticky top-4 border shadow-lg">
                <CardContent className="p-0">
                  {/* Property Image */}
                  <div className="aspect-video w-full bg-gray-100 overflow-hidden rounded-t-lg">
                    {mainImage ? (
                      <img
                        src={mainImage}
                        alt={space.name}
                        className="w-full h-full object-cover"
                        onError={e => {
                          e.target.style.display = "none";
                          e.target.nextElementSibling.style.display = "flex";
                        }}
                      />
                    ) : null}
                    <div
                      className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100"
                      style={{ display: mainImage ? "none" : "flex" }}
                    >
                      <Building2 className="w-16 h-16 text-gray-400" />
                    </div>
                  </div>

                  <div className="p-5 space-y-4">
                    {/* Property Info */}
                    <div>
                      <h3 className="font-semibold text-base text-gray-900">{space.name}</h3>
                      <p className="text-sm text-gray-600 mt-0.5">
                        {(typeof space.subCategory === "string" ? space.subCategory : space.subCategory?.name) ||
                          (typeof space.type === "string" ? space.type : space.type?.name) ||
                          "Workspace"}
                      </p>
                    </div>

                    <Separator />

                    {/* Booking Summary */}
                    <div className="space-y-2.5">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <Users className="h-4 w-4 text-gray-500" />
                        <span>
                          {space.capacity || 1} guest{(space.capacity || 1) > 1 ? "s" : ""}
                        </span>
                      </div>
                      {selectedDate && (
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <CalendarIcon className="h-4 w-4 text-gray-500" />
                          <span>{formatDate(selectedDate)}</span>
                        </div>
                      )}
                    </div>

                    <Separator />

                    {/* Price Breakdown */}
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="underline decoration-dotted decoration-gray-400">
                          {isMonthlyDuration ? (
                            <>
                              {userCurrency} {monthlyRate.toFixed(2)} ×{" "}
                              {monthlyPeriods ? `${monthlyPeriods} month${monthlyPeriods === 1 ? "" : "s"}` : "—"}
                            </>
                          ) : (
                            <>
                              {userCurrency} {hourlyRate.toFixed(2)} ×{" "}
                              {bookingDetails ? `${durationHours.toFixed(1)} hours` : "—"}
                            </>
                          )}
                        </span>
                        <span className="font-medium">
                          {basePrice > 0 ? `${userCurrency} ${basePrice.toFixed(2)}` : "--"}
                        </span>
                      </div>

                      {selectedAddons.length > 0 && (
                        <div className="space-y-1.5 pt-1">
                          {selectedAddons.map((addon, index) => (
                            <div key={index} className="flex justify-between text-xs text-gray-600">
                              <span>
                                {addon.name} x {addon.quantity}
                              </span>
                              <span>
                                {userCurrency} {(addon.price * addon.quantity).toFixed(2)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex justify-between font-semibold text-base pt-1 border-t border-gray-100">
                        <span>Total ({userCurrency})</span>
                        <span>{totalPrice > 0 ? totalPrice.toFixed(2) : "--"}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-white flex justify-between items-center">
          <Button onClick={onClose} variant="ghost" size="lg" className="text-gray-700 hover:text-gray-900">
            Cancel
          </Button>
          <div className="flex gap-3">
            {!viewOnly && hasActiveSubscription && !bookingError && (
              <Button
                onClick={() => handleSubmitBooking(false)}
                disabled={!isFormValid}
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8"
              >
                {isFormValid ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Book Now
                  </>
                ) : (
                  "Select time slot"
                )}
              </Button>
            )}
            {!viewOnly && (!hasActiveSubscription || bookingError) && (
              <Button
                onClick={() => handleSubmitBooking(true)}
                disabled={!isFormValid}
                size="lg"
                className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-8"
              >
                {isFormValid ? (
                  <>
                    <CreditCard className="h-4 w-4 mr-2" />
                    Pay & Book
                  </>
                ) : (
                  "Select time slot"
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HSpaceDetailsModal;
